"use client";

import { DefaultChatTransport } from "ai";
import { useChat } from "@ai-sdk/react";
import { use, useEffect, useRef, useState } from "react";
import { useSWRConfig } from "swr";
import { ChatHeader } from "@/components/chat-header";
import { fetchWithErrorHandlers, generateUUID } from "@/lib/utils";
import { Artifact } from "./artifact";
import { MultimodalInput } from "./multimodal-input";
import { Messages } from "./messages";
import { useArtifactSelector, useArtifact } from "@/hooks/use-artifact";
import { unstable_serialize } from "swr/infinite";
// import { getChatHistoryPaginationKey } from "./sidebar-history";
import { useChatVisibility } from "@/hooks/use-chat-visibility";
import { ChatSDKError } from "@/lib/errors";
import { useDataStream } from "./data-stream-provider";
import { addHistoryItem } from "@/lib/history-updater";
import AfterPaymentSuccessModel from "./congraculation.jsx";
import { useSelector, useDispatch } from "react-redux";
import FloatingHelpSidebar from "@/components/floating";
import { isDocumentType } from "@/lib/dropDownData";
import { ExploreBanner } from "./explore-banner";

import {
  setShareData,
  setIsCreateModeifyWebsite,
  setLiveWebsiteUrl,
  setUser_ai_limit,
  setIsDeductLimit,
  setUserIdParent, setActiveScreenId,
  setSaveDbStore
} from "@/store/mainSlice";
import { useRouter, usePathname } from "next/navigation";
import { decryptHtml, encryptHtml } from "../lib/cryptoUtils"
import { toast } from 'sonner';


export function Chat({
  id,
  initialMessages,
  initialChatModel,
  initialVisibilityType,
  isReadonly,
  autoResume,
  initialHtmlContent,
  initialScreenId,
  urlData,
  setSavedHtmlUrl, chatData
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const pathname = usePathname();

  const { visibilityType } = useChatVisibility({
    chatId: id,
    initialVisibilityType,
  });

  const { setArtifact, artifact } = useArtifact();
  const { mutate } = useSWRConfig();
  const { setDataStream } = useDataStream();

  const [input, setInput] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  // const [userId, setUserId] = useState(null);
  const isArtifactVisible = useArtifactSelector((state) => state.isVisible);
  const [isSaveHtmlToDb, setIsSaveHtmlToDb] = useState(false);
  const [screenId, setScreenId] = useState(initialScreenId || null);
  const [aiLimit, setAiLimit] = useState(0);
  const [remainingCredits, setRemainingCredits] = useState(0);
  const [creditsUsed, setCreditsUsed] = useState(0);
  // const [remainingWords, setRemainingWords] = useState(0);
  // const [userDetail, setUserDetail] = useState(null);
  // const [isFreeLimit, setIsFreeLimit] = useState(true);
  const [isFollowUp, setIsFollowUp] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [showAfterPaymentPopup, setShowAfterPaymentPopup] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const divRef = useRef(null);
  const [hasMounted, setHasMounted] = useState(false);
  const { activeTab, isPaymentSuccess, userData } =
    useSelector((state) => state.main);

  const isProjectPage = pathname.startsWith("/project");
  const effectiveTab = isProjectPage ? activeTab : activeTab ?? "Page Builder";
  let chatType = "chat";

  useEffect(() => {
    setHasMounted(true);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      router.replace("/login");
    }
  }, []);

  useEffect(() => {
    if (urlData) {
      dispatch(setLiveWebsiteUrl(urlData));
    }
  }, [urlData]);

  const {
    messages,
    setMessages,
    sendMessage,
    status,
    stop,
    regenerate,
    resumeStream,
  } = useChat({
    id,
    messages: initialMessages,
    experimental_throttle: 100,
    generateId: generateUUID,
    transport: new DefaultChatTransport({
      api: "/api/chat",
      fetch: fetchWithErrorHandlers,
      prepareSendMessagesRequest({ messages, id, body }) {
        return {
          body: {
            id,
            message: messages.at(-1),
            selectedChatModel: initialChatModel,
            selectedVisibilityType: visibilityType,
            ...body,
          },
        };
      },
    }),
    onData: (dataPart) => {
      setDataStream((ds) => (ds ? [...ds, dataPart] : []));
    },
    onFinish: async () => {
      // mutate(unstable_serialize(getChatHistoryPaginationKey));
      if (initialMessages.length === 0) {
        try {
          const res = await fetch(
            `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website/logs/${initialScreenId}`
          );
          const data = await res.json();
          if (data.status === 1 && data.logs.length > 0) {
            const firstLog = data.logs[0];
            addHistoryItem({
              screen_id: firstLog.screen_id,
              prompt: firstLog.prompt,
              html_file_detail: firstLog.html_file_detail,
              creditUsed: firstLog.creditUsed,
              is_followup: firstLog.is_followup,
              createdAt:
                firstLog.createdAt ??
                firstLog.created_at ??
                firstLog.timestamp ??
                new Date().toISOString(),
            });
          }
        } catch (err) {
          console.error("Failed to fetch new chat for sidebar", err);
        }
      }
    },
    onError: (error) => {
      if (error instanceof ChatSDKError) {
        toast({
          type: "error",
          description: error.message,
        });
      }
    },
  });

  useEffect(() => {
    if (messages.length > 0) {
      const lastUserMsg = [...messages].reverse().find((m) => m.role === "user");
      if (lastUserMsg?.parts?.length > 0) {
        setPrompt(lastUserMsg.parts[0].text);
      }
    }
  }, [messages]);

  useEffect(() => {
    if (artifact.isPaymentSuccess) {
      setShowAfterPaymentPopup(true);
      setArtifact((old) => ({ ...old, isPaymentSuccess: false }));
    }
  }, [artifact]);

  if (effectiveTab === "Card Maker") {
    chatType = "card_maker";
  } else if (effectiveTab === "Page Builder") {
    chatType = "page_builder";
  } else if (effectiveTab === "Go-to-Market Strategy") {
    chatType = "go_to_market_strategy";
  } else if (effectiveTab) {
    chatType = effectiveTab.toLowerCase().replace(/[\s\/]+/g, "_");
  }
  // else {
  //   chatType = activeTab.toLowerCase().replace(/[\s\/]+/g, "_");
  // }

  const savePromptToDB = async (prompt, htmlContent, isFollowUp) => {
    const isNewScreen = !screenId;

    // try {
    const formData = new FormData();
    formData.append("user_id", userData._id);
    formData.append("prompt", prompt);
    formData.append("is_followup", isFollowUp);
    formData.append("creditUsed", creditsUsed);
    formData.append("isNewScreen", isNewScreen);
    formData.append("chat_type", chatType);
    formData.append(
      "system_message",
      `Your ${chatType === "card_maker" ? "Card" : chatType === "page_builder" ? "Web Page" : activeTab} is ready! Take a look - If you have any updates, just type and will update it for you.`
    );

    if (screenId) {
      formData.append("screen_id", screenId); 
    }

    if (isDocumentType(chatType)) {
      formData.append("content", htmlContent); 
    } else {
      formData.append("html_file_detail", htmlContent);
    }
    // Only append file if available
    if (uploadImage) {
      formData.append("image", uploadImage); 
    }

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_APP_URL_LOCAL}/api/website`,
      {
        method: "POST",
        body: formData, 
      }
    );

    const data = await response.json();
    let decryptDetails = data.detail;
    dispatch(setSaveDbStore(decryptDetails))
    dispatch(setLiveWebsiteUrl(decryptDetails?.html_file_detail));

    const shareData = {
      websiteUrl: decryptDetails.html_file_detail,
      screenId: decryptDetails.screen_id,
      websiteDetailsUid: decryptDetails._id,
      userId: userData._id,
    };
    dispatch(setShareData(shareData));

    if (isNewScreen && data.screen_id) {
      setScreenId(data.screen_id);
      dispatch(setActiveScreenId(data.screen_id));
      localStorage.setItem("screenId", data.screen_id);

      const redesignPending = localStorage.getItem("redesign_pending") === "1";
      const promptLooksLikeRedesign =
        typeof prompt === "string" &&
        prompt.toLowerCase().startsWith("redesign the website at");
      if (redesignPending || promptLooksLikeRedesign) {
        const redesignFlags = JSON.parse(localStorage.getItem("redesignFlags") || "{}");
        redesignFlags[String(data.screen_id)] = true;
        localStorage.setItem("redesignFlags", JSON.stringify(redesignFlags));
        localStorage.removeItem("redesign_pending");
      }

      addHistoryItem({
        _id: data._id,
        prompt,
        html_file_detail: htmlContent,
        is_followup: false,
        creditUsed: creditsUsed,
        createdAt: new Date().toISOString(),
        screen_id: data.screen_id,
      });
    }

    if (data?.credits_left !== undefined) {
      setAiLimit(data.credits_left);
      setRemainingCredits(data.credits_left);
      dispatch(setUser_ai_limit(data.credits_left));
      dispatch(
        setIsCreateModeifyWebsite({ userId: userData._id, websiteId: decryptDetails._id })
      );
      localStorage.setItem(
        "userInfo",
        JSON.stringify({ ...userData, ai_limit: data.credits_left })
      );
    }
    router.replace(`/${data.screen_id}`);
  };

  useEffect(() => {
    if (isSaveHtmlToDb) {
      savePromptToDB(prompt, htmlContent, isFollowUp);
      setArtifact((prev) => ({ ...prev, content: htmlContent }));
      setIsSaveHtmlToDb(false);
    }
  }, [isSaveHtmlToDb, htmlContent, prompt, isFollowUp, creditsUsed]);

  useEffect(() => {
    if (initialHtmlContent) setHtmlContent(initialHtmlContent);
  }, [initialHtmlContent]);

  useEffect(() => {
    if (isArtifactVisible) {
      document.body.classList.add("hide-overflow");
    } else {
      document.body.classList.remove("hide-overflow");
    }
  }, [isArtifactVisible]);

  useEffect(() => {
    let artifactClosed = false; 
    const handlePopState = () => {
      if (isArtifactVisible) {
        setArtifact((prev) => ({ ...prev, isVisible: false }));
        artifactClosed = true;
        window.history.pushState({}, "", window.location.href);
      } else if (artifactClosed) {
        if (window.location.pathname.startsWith("/scr-")) {
          window.location.href = "/";
        }
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => window.removeEventListener("popstate", handlePopState);
  }, [isArtifactVisible, setArtifact]);

  useEffect(() => {
    window.history.pushState(null, "", window.location.href);

    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, "", window.location.href);
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    if (chatData?.chat_type && chatData.chat_type !== chatType) {
      router.replace("/");
    }
  }, [chatData, chatType]);

  useEffect(() => {
    setTimeout(() => {
      if (divRef.current) {
        divRef.current.scrollTop = divRef.current.scrollHeight;
      }
    }, 0);
  }, [isArtifactVisible]);
  if (!hasMounted) return null;

  return (
    <>
      <div className="flex flex-col min-w-0 bg-background border-0" data-chat-scroll-container="true" ref={divRef} style={{
        height: "100vh",
        overflowY: "auto",
      }}>
        <ChatHeader
          chatId={id}
          selectedModelId={initialChatModel}
          selectedVisibilityType={initialVisibilityType}
          isReadonly={isReadonly}
        />
                <div className="mx-auto w-full px-4 mt-6 md:max-w-[896px]">
                  <ExploreBanner
                    type={activeTab ?? "Page Builder"}
                    collapsed={messages.length > 0}
                  />
                </div>
        <div
          className={`flex flex-col justify-center items-center flex-1 transition-all duration-300 mx-auto w-full px-4 ${isArtifactVisible ? "md:max-w-sm" : "md:max-w-[896px]"
            }`}
        >
          <Messages
            chatId={id}
            status={status}
            messages={messages}
            setMessages={setMessages}
            regenerate={regenerate}
            isReadonly={isReadonly}
            isArtifactVisible={isArtifactVisible}
            showCode={showCode}
            initialScreenId={initialScreenId}
          />

          {!isReadonly && (
            <MultimodalInput
              screenId={screenId}
              input={input}
              setInput={setInput}
              status={status}
              stop={stop}
              messages={messages}
              setMessages={setMessages}
              sendMessage={sendMessage}
              selectedVisibilityType={visibilityType}
              htmlContent={htmlContent}
              setHtmlContent={setHtmlContent}
              setIsSaveHtmlToDb={setIsSaveHtmlToDb}
              isFollowUp={isFollowUp}
              aiLimit={aiLimit}
              remainingCredits={remainingCredits}
              setRemainingCredits={setRemainingCredits}
              setIsFollowUp={setIsFollowUp}
              setCreditsUsedParent={setCreditsUsed}
              setUploadImage={setUploadImage}
            />
          )}
        </div>
        <FloatingHelpSidebar />
      </div>

      {isArtifactVisible && (
        <Artifact
          screenId={screenId}
          input={input}
          setInput={setInput}
          status={status}
          stop={stop}
          sendMessage={sendMessage}
          messages={messages}
          setMessages={setMessages}
          regenerate={regenerate}
          isReadonly={isReadonly}
          selectedVisibilityType={visibilityType}
          htmlContent={htmlContent}
          setHtmlContent={setHtmlContent}
          setIsSaveHtmlToDb={setIsSaveHtmlToDb}
          isFollowUp={isFollowUp}
          aiLimit={aiLimit}
          remainingCredits={remainingCredits}
          setRemainingCredits={setRemainingCredits}
          setIsFollowUp={setIsFollowUp}
          setCreditsUsedParent={setCreditsUsed}
          setSavedHtmlUrl={setSavedHtmlUrl}
        />
      )}

      <AfterPaymentSuccessModel
        open={showAfterPaymentPopup}
        onClose={() => setShowAfterPaymentPopup(false)}
      />

      {isPaymentSuccess && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-white"></div>
        </div>
      )}
    </>
  );
}
